import React, { useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import SyncOutlinedIcon from '@material-ui/icons/SyncOutlined';
import SettingsCard from 'components/kea/SettingsCard/SettingsCard';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import { useCatalogUpdatePolicy } from 'pluginAdmin/features/catalogUpdates/useCatalogUpdatePolicy';
import { useShellAppContext } from '../shell/context/useShellAppContext';
import DataSettingsToggle from './DataSettingsToggle';

const CRON_EXAMPLE =
  '0 2 * * * /usr/bin/php /path/to/prestashop/modules/keaprosearch/tools/runtime/full-reindex.php';

const COMPLETION_PHASE_KEYS = {
  completion_admission: 'catalogUpdates.dataSettings.completion.phase.verifying',
  feed_coverage: 'catalogUpdates.dataSettings.completion.phase.feedCoverage',
  runtime_finalize: 'catalogUpdates.dataSettings.completion.phase.savingStatus',
  completion_adapter: 'catalogUpdates.dataSettings.completion.phase.finishing',
  terminal_ack: 'catalogUpdates.dataSettings.completion.phase.savingStatus',
};

const COMPLETION_REASON_KEYS = {
  job_owner_conflict: 'catalogUpdates.dataSettings.completion.reason.ownershipChanged',
  feed_owner_conflict: 'catalogUpdates.dataSettings.completion.reason.ownershipChanged',
  runtime_identity_invalid: 'catalogUpdates.dataSettings.completion.reason.ownershipChanged',
  runtime_state_unavailable: 'catalogUpdates.dataSettings.completion.reason.stateUnavailable',
  coverage_persist_failed: 'catalogUpdates.dataSettings.completion.reason.savingFailed',
  bookkeeping_persist_failed: 'catalogUpdates.dataSettings.completion.reason.savingFailed',
  job_terminal_persist_failed: 'catalogUpdates.dataSettings.completion.reason.savingFailed',
  adapter_failed: 'catalogUpdates.dataSettings.completion.reason.integrationFailed',
};

function runtimeTimestamp(value) {
  if (!value) return null;
  const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(value)
    ? `${value.replace(' ', 'T').replace(/(\.\d{3})\d+$/, '$1')}Z`
    : value;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

const useStyles = makeStyles(theme => ({
  card: { marginTop: theme.spacing(3), marginBottom: theme.spacing(2) },
  layout: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      alignItems: 'flex-start',
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
  },
  identity: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    minWidth: 0,
  },
  icon: {
    display: 'flex',
    width: 40,
    height: 40,
    flex: '0 0 auto',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    background: 'var(--kea-color-primary-soft)',
    color: 'var(--kea-color-primary)',
  },
  copy: { maxWidth: 650 },
  description: { marginTop: theme.spacing(0.5) },
  notice: { marginTop: theme.spacing(2) },
  runtimeDetail: { marginTop: theme.spacing(0.5) },
  completionTitle: { fontWeight: 600, marginBottom: theme.spacing(0.5) },
  completionActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
  },
  completionAction: { minHeight: 44 },
  feedback: {
    marginTop: theme.spacing(1.5),
    color: 'var(--kea-color-primary)',
  },
  cron: {
    marginTop: theme.spacing(2),
    '& summary': {
      cursor: 'pointer',
      color: 'var(--kea-color-primary)',
      padding: theme.spacing(1, 0),
    },
    '& summary:focus-visible': {
      outline: '2px solid var(--kea-color-primary)',
      outlineOffset: 2,
    },
  },
  command: {
    maxWidth: '100%',
    overflowX: 'auto',
    padding: theme.spacing(2),
    margin: theme.spacing(1, 0),
    borderRadius: 8,
    background: 'var(--kea-color-primary-soft)',
    fontSize: '0.8125rem',
  },
}));

export default function CatalogUpdatePolicySection() {
  const classes = useStyles();
  const t = useLocalizedMessage();
  const { compatibility, bridgeClient } = useShellAppContext();
  const supported = compatibility?.catalogUpdatePolicy?.managed === true;
  // This compatibility gate already requires the PrestaShop plugin and its exact capabilities.
  const cronHelpAvailable =
    supported && compatibility?.catalogUpdatePolicy?.scheduleManaged === true;
  const {
    api,
    state,
    setState,
    isLoading,
    error,
    setError,
    reload,
  } = useCatalogUpdatePolicy({ enabled: supported, bridgeClient });
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [completionAction, setCompletionAction] = useState(null);
  const [completionFeedback, setCompletionFeedback] = useState(null);

  if (!supported) return null;

  const knownState =
    state &&
    ['automatic', 'scheduled', 'paused'].includes(state.mode) &&
    Number.isInteger(state.revision) &&
    state.revision >= 0 &&
    Array.isArray(state.supportedModes);
  const enabled = state?.mode === 'automatic';
  const canEnable =
    state?.automaticSupported === true &&
    state.supportedModes?.includes('automatic');
  const canDisable = state?.supportedModes?.includes('scheduled');
  const visibleError = saveError || error;
  const isMutating = isSaving || completionAction !== null || isLoading;
  const controlReady = knownState && !isLoading && !visibleError;
  const disabled = isMutating || (enabled ? !canDisable : !canEnable);
  const runtime = state?.runtime || null;
  const completionStatusUnavailable = state?.completionRecoverySupported === true
    && runtime?.completionStatusAvailable === false;
  const completionAttention = state?.completionRecoverySupported === true
    && runtime?.completionStatusAvailable === true
    && runtime.completionAttentionRequired === true;
  const completionControlsReady = completionAttention
    && Boolean(runtime.completionJobId)
    && Boolean(runtime.completionVersion);
  const completionRetryAt = runtimeTimestamp(runtime?.completionRetryAt);
  const runtimeProblem =
    state?.runtime?.state === 'failed' ||
    state?.runtime?.state === 'unavailable' ||
    state?.runtime?.manualRecoveryRequired === true;
  const needsRetry = visibleError || (!isLoading && !knownState)
    || (runtimeProblem && !completionAttention && !completionStatusUnavailable);

  const handleToggle = async event => {
    const nextEnabled = event.target.checked;
    if (!controlReady || disabled || nextEnabled === enabled) return;
    const mode = nextEnabled ? 'automatic' : 'scheduled';
    setIsSaving(true);
    setSaveError(null);
    setError(null);
    setCompletionFeedback(null);
    try {
      setState(await api.setMode(mode, state.revision));
    } catch (nextError) {
      setSaveError(nextError);
      // A failed response cannot prove whether the write was applied. Read back before another change.
      reload();
    } finally {
      setIsSaving(false);
    }
  };

  const runCompletionAction = async action => {
    const allowed = action === 'retry'
      ? runtime?.completionRetryAllowed === true
      : runtime?.completionStopAllowed === true;
    if (!controlReady || !completionControlsReady || !allowed || isMutating) return;

    setCompletionAction(action);
    setSaveError(null);
    setError(null);
    setCompletionFeedback(null);
    try {
      const next = action === 'retry'
        ? await api.retryCompletion(runtime.completionJobId, runtime.completionVersion)
        : await api.stopCompletion(runtime.completionJobId, runtime.completionVersion);
      setState(next);
      setCompletionFeedback(action);
    } catch (nextError) {
      setSaveError(nextError);
      if (nextError?.code === 'REVISION_CONFLICT'
        || nextError?.code === 'RECOVERY_UNAVAILABLE') {
        try {
          setState(await api.getStatus());
        } catch (refreshError) {
          setError(refreshError);
        }
      }
    } finally {
      setCompletionAction(null);
    }
  };

  const errorMessage = {
    MODE_UNAVAILABLE: 'catalogUpdates.dataSettings.modeUnavailable',
    RECOVERY_UNAVAILABLE: 'catalogUpdates.dataSettings.completion.recoveryUnavailable',
    RECOVERY_FAILED: 'catalogUpdates.dataSettings.completion.actionError',
  }[visibleError?.code] || (runtimeProblem && !visibleError
    ? 'catalogUpdates.dataSettings.realtime.failed'
    : 'catalogUpdates.dataSettings.error');

  return (
    <SettingsCard
      className={classes.card}
      data-testid="catalog-update-policy-card"
    >
      <Box className={classes.layout}>
        <Box className={classes.identity}>
          <Box className={classes.icon} aria-hidden="true">
            {isMutating ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <SyncOutlinedIcon />
            )}
          </Box>
          <Box className={classes.copy}>
            <Typography variant="h6">
              {t('catalogUpdates.dataSettings.realtime.title')}
            </Typography>
            <Typography
              id="catalog-update-description"
              variant="body2"
              color="textSecondary"
              className={classes.description}
            >
              {t('catalogUpdates.dataSettings.realtime.description')}
            </Typography>
          </Box>
        </Box>
        {controlReady && (
          <DataSettingsToggle
            checked={enabled}
            disabled={disabled}
            onChange={handleToggle}
            label={t('catalogUpdates.dataSettings.realtime.title')}
            describedBy="catalog-update-description"
            testId="catalog-update-realtime-toggle"
          />
        )}
        {isLoading && (
          <Typography variant="body2" color="textSecondary" role="status">
            {t('catalogUpdates.dataSettings.realtime.loading')}
          </Typography>
        )}
      </Box>

      {needsRetry && (
        <Alert
          severity="error"
          className={classes.notice}
          action={
            <Button
              color="inherit"
              size="small"
              disabled={isMutating}
              onClick={() => {
                setError(null);
                setSaveError(null);
                setCompletionFeedback(null);
                reload();
              }}
            >
              {t('catalogUpdates.dataSettings.retry')}
            </Button>
          }
        >
          {t(errorMessage)}
        </Alert>
      )}

      {completionAttention && (
        <Alert
          severity={runtime.activeIndexPresent ? 'warning' : 'error'}
          className={classes.notice}
          data-testid="catalog-update-completion-recovery"
          role="alert"
        >
          <Typography variant="subtitle2" className={classes.completionTitle}>
            {t(runtime.activeIndexPresent
              ? 'catalogUpdates.dataSettings.completion.searchAvailable'
              : 'catalogUpdates.dataSettings.completion.searchUnavailable')}
          </Typography>
          <Typography variant="body2">
            {t('catalogUpdates.dataSettings.completion.description')}
          </Typography>
          <Typography variant="caption" component="div" className={classes.runtimeDetail}>
            {t('catalogUpdates.dataSettings.completion.phaseLabel', {
              phase: t(COMPLETION_PHASE_KEYS[runtime.completionPhase]
                || 'catalogUpdates.dataSettings.completion.phase.unknown'),
            })}
          </Typography>
          <Typography variant="caption" component="div" className={classes.runtimeDetail}>
            {t('catalogUpdates.dataSettings.completion.reasonLabel', {
              reason: t(COMPLETION_REASON_KEYS[runtime.completionReason]
                || 'catalogUpdates.dataSettings.completion.reason.unknown'),
            })}
          </Typography>
          {runtime.completionFailureAttempts > 0 && (
            <Typography variant="caption" component="div" className={classes.runtimeDetail}>
              {t('catalogUpdates.dataSettings.completion.attempts', {
                count: runtime.completionFailureAttempts,
              })}
            </Typography>
          )}
          {completionRetryAt && !runtime.completionStopped && (
            <Typography variant="caption" component="div" className={classes.runtimeDetail}>
              {t('catalogUpdates.dataSettings.completion.retryAt', {
                date: completionRetryAt.toLocaleString(),
              })}
            </Typography>
          )}
          {runtime.completionStopped && (
            <Typography variant="caption" component="div" className={classes.runtimeDetail}>
              {t('catalogUpdates.dataSettings.completion.stopped')}
            </Typography>
          )}
          {completionControlsReady
            && (runtime.completionRetryAllowed || runtime.completionStopAllowed) && (
            <Box className={classes.completionActions}>
              {runtime.completionRetryAllowed && (
                <Button
                  className={classes.completionAction}
                  size="small"
                  color="primary"
                  variant="contained"
                  disabled={isMutating || Boolean(visibleError)}
                  onClick={() => runCompletionAction('retry')}
                  startIcon={completionAction === 'retry'
                    ? <CircularProgress size={14} color="inherit" />
                    : undefined}
                >
                  {t('catalogUpdates.dataSettings.completion.retry')}
                </Button>
              )}
              {runtime.completionStopAllowed && (
                <Button
                  className={classes.completionAction}
                  size="small"
                  color="secondary"
                  variant="outlined"
                  disabled={isMutating || Boolean(visibleError)}
                  onClick={() => runCompletionAction('stop')}
                  startIcon={completionAction === 'stop'
                    ? <CircularProgress size={14} color="inherit" />
                    : undefined}
                >
                  {t('catalogUpdates.dataSettings.completion.stop')}
                </Button>
              )}
            </Box>
          )}
        </Alert>
      )}

      {completionStatusUnavailable && (
        <Alert
          severity="warning"
          className={classes.notice}
          data-testid="catalog-update-completion-status-unavailable"
          role="alert"
          action={(
            <Button
              color="inherit"
              size="small"
              disabled={isMutating || Boolean(visibleError)}
              onClick={() => {
                setError(null);
                setSaveError(null);
                setCompletionFeedback(null);
                reload();
              }}
            >
              {t('catalogUpdates.dataSettings.completion.refresh')}
            </Button>
          )}
        >
          {t('catalogUpdates.dataSettings.completion.statusUnavailable')}
        </Alert>
      )}

      {completionFeedback && !visibleError && (
        <Typography className={classes.feedback} variant="body2" role="status">
          {t(completionFeedback === 'retry'
            ? 'catalogUpdates.dataSettings.completion.retryRequested'
            : 'catalogUpdates.dataSettings.completion.stopRequested')}
        </Typography>
      )}

      {cronHelpAvailable && (
        <details
          className={classes.cron}
          data-testid="catalog-update-cron-help"
        >
          <summary>{t('catalogUpdates.dataSettings.cron.title')}</summary>
          <Typography variant="body2" color="textSecondary">
            {t('catalogUpdates.dataSettings.cron.description')}
          </Typography>
          <Alert severity="warning" role="note" className={classes.notice}>
            {t('catalogUpdates.dataSettings.cron.paths')}
          </Alert>
          <pre className={classes.command}>
            <code>{CRON_EXAMPLE}</code>
          </pre>
        </details>
      )}
    </SettingsCard>
  );
}
